// app/(app)/(root)/about.tsx (or wherever your route lives)
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FlatList, Platform, RefreshControl, StyleSheet, View } from "react-native";
import * as Application from "expo-application";
import * as Updates from "expo-updates";
import Constants from "expo-constants";

import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";

import { ThemedText } from "@/components/ThemedText";
import { ModernCard, StatusBadge, ActionButton } from "@/components/crew";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import * as pkg from "@/package.json";

type Tenant = {
  id: string;
  name: string;
  created_at: string;
};

const FALLBACK_TENANT: Tenant = {
  id: "f7a4f585-c7fa-4647-b305-e790f53ead31",
  name: "Sample Co.",
  created_at: new Date().toISOString(),
};

async function fetchTenants(): Promise<Tenant[]> {
  const { data, error } = await supabase.from("tenants").select("*").order("created_at", { ascending: false });

  if (error) throw new Error(error.message);
  return (data ?? []) as Tenant[];
}

export default function AboutScreen() {
  const { signOut, user } = useAuth();
  const { colors } = useTheme();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runtimeVersion = Updates.runtimeVersion ?? (Constants?.manifest2 as any)?.runtimeVersion ?? "unknown";

  const nativeVersion = Application.nativeBuildVersion ?? "unknown";
  const updateChannel = (Updates as any)?.channel ?? (Updates.manifest as any)?.channel ?? Updates.channel ?? "dev";
  const nodeEnv = process.env.NODE_ENV ?? "unknown";

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await fetchTenants();
      setTenants(data.length ? data : [FALLBACK_TENANT]);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load tenants");
      // still show a friendly example so the page never feels empty
      setTenants([FALLBACK_TENANT]);
    } finally {
      setLoading(false);
    }
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await fetchTenants();
      setTenants(data.length ? data : [FALLBACK_TENANT]);
    } catch (e) {
      // keep existing list; silent fail into non-blocking toast if you have one
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    // initial load
    load();
  }, [load]);

  const header = useMemo(
    () => (
      <View style={styles.headerBlock}>
        {/* Profile Section */}
        <ModernCard style={styles.profileCard} margin={0}>
          <View style={styles.profileHeader}>
            <MaterialCommunityIcons name="account-circle" size={24} color={colors.primary} />
            <ThemedText style={styles.profileTitle}>Profile & Settings</ThemedText>
          </View>
          <View style={styles.profileInfo}>
            <ThemedText style={styles.userEmail}>{user?.email || "Not signed in"}</ThemedText>
            <StatusBadge variant="upcoming" text="AUTHENTICATED" icon="check-circle" size="small" />
          </View>
        </ModernCard>

        {/* App Information */}
        <ModernCard style={styles.infoCard} margin={0}>
          <View style={styles.cardTitleRow}>
            <MaterialCommunityIcons name="information-outline" size={20} color={colors.text} />
            <ThemedText style={styles.cardTitle}>App Information</ThemedText>
          </View>
          <View style={styles.infoGrid}>
            {Platform.OS === "web" ? (
              <InfoRow label="App Version" value={String(pkg.version)} mono /> //
            ) : (
              <InfoRow label="Runtime Version" value={String(runtimeVersion)} mono />
            )}
            {Platform.OS !== "web" && <InfoRow label="Native Build" value={nativeVersion} mono />}
            <InfoRow label="Update Channel" value={String(updateChannel)} mono />
            <InfoRow label="Environment" value={nodeEnv} mono />
          </View>
        </ModernCard>

        {/* Actions */}
        <ModernCard style={styles.actionsCard} margin={0}>
          <View style={styles.cardTitleRow}>
            <MaterialCommunityIcons name="cog-outline" size={20} color={colors.text} />
            <ThemedText style={styles.cardTitle}>Actions</ThemedText>
          </View>
          <View style={styles.actionButtons}>
            <ActionButton title="Refresh" icon="refresh" onPress={onRefresh} variant="secondary" size="small" />
            <ActionButton
              title="Check Updates"
              icon="update"
              onPress={async () => {
                try {
                  const res = await Updates.checkForUpdateAsync();
                  if (res.isAvailable) await Updates.fetchUpdateAsync().then(() => Updates.reloadAsync());
                } catch {
                  // swallow; you can wire to your toast/snackbar here
                }
              }}
              variant="primary"
              size="small"
            />
            <ActionButton title="Sign Out" icon="logout" onPress={signOut} variant="danger" size="small" />
          </View>
        </ModernCard>

        {error && <ThemedText style={styles.errorText}>{error}</ThemedText>}

        {/* Tenants Section */}
        <View style={styles.sectionHeader}>
          <MaterialCommunityIcons name="domain" size={20} color={colors.text} />
          <ThemedText style={styles.sectionLabel}>Organizations</ThemedText>
        </View>
      </View>
    ),
    [user, colors, runtimeVersion, nativeVersion, updateChannel, nodeEnv, onRefresh, signOut, error],
  );

  return (
    <FlatList
      data={tenants}
      keyExtractor={(item) => item.id}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      ListHeaderComponent={header}
      ItemSeparatorComponent={() => <View style={styles.separator} />}
      contentContainerStyle={styles.listContent}
      renderItem={({ item }) => <TenantRow item={item} />}
    />
  );
}

/** ---------- little presentational bits ---------- **/

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <View style={styles.infoRow}>
      <ThemedText style={styles.infoLabel}>{label}</ThemedText>
      <ThemedText style={[styles.infoValue, mono && styles.mono]} numberOfLines={1}>
        {value}
      </ThemedText>
    </View>
  );
}

function TenantRow({ item }: { item: Tenant }) {
  const { colors } = useTheme();

  return (
    <ModernCard style={styles.tenantCard} margin={0}>
      <View style={styles.tenantHeader}>
        <View style={styles.tenantInfo}>
          <MaterialCommunityIcons name="domain" size={20} color={colors.emerald?.text} />
          <ThemedText style={styles.tenantName}>{item.name}</ThemedText>
        </View>
        <StatusBadge variant="upcoming" text="ACTIVE" icon="check-circle" size="small" />
      </View>
      <ThemedText style={[styles.tenantId, { color: colors.textSecondary }]} numberOfLines={1}>
        ID: {item.id}
      </ThemedText>
      <ThemedText style={[styles.tenantDate, { color: colors.textSecondary }]}>
        Created {new Date(item.created_at).toLocaleDateString()}
      </ThemedText>
    </ModernCard>
  );
}

/** ---------- styles ---------- **/

const styles = StyleSheet.create({
  container: { flex: 1 },
  listContent: { padding: 12, paddingBottom: 32 },

  headerBlock: { gap: 8, marginBottom: 8 },

  // Profile card
  profileCard: {},
  profileHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  profileTitle: {
    fontSize: 18,
    fontWeight: "600",
  },
  profileInfo: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  userEmail: {
    fontSize: 16,
    fontWeight: "500",
    flex: 1,
  },

  // Cards
  infoCard: {},
  actionsCard: {},
  cardTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: "600",
  },

  // Info grid
  infoGrid: {
    gap: 8,
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  infoLabel: { width: 120, opacity: 0.7, fontSize: 13 },
  infoValue: { flex: 1, fontWeight: "500", fontSize: 13 },
  mono: { fontFamily: Platform.select({ default: "Menlo", android: "monospace" }) as any },

  // Action buttons
  actionButtons: {
    flexDirection: "row",
    gap: 8,
    flexWrap: "wrap",
  },

  // Section header
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 4,
    marginBottom: 8,
  },
  sectionLabel: {
    fontSize: 18,
    fontWeight: "600",
  },

  // Hero
  hero: {
    height: 200,
    justifyContent: "center",
  },
  heroContent: {
    alignItems: "center",
    gap: 16,
  },
  heroProfile: {
    alignItems: "center",
    gap: 8,
  },
  heroWelcome: {
    fontSize: 16,
    color: "white",
    opacity: 0.9,
  },
  heroUserName: {
    fontSize: 24,
    fontWeight: "700",
    color: "white",
    textTransform: "capitalize",
  },
  heroAppInfo: {
    alignItems: "center",
    gap: 4,
  },
  heroLogo: {
    height: 40,
    width: 40,
    opacity: 0.9,
  },
  heroAppName: {
    fontSize: 20,
    fontWeight: "800",
    color: "white",
    letterSpacing: 1,
  },
  heroTag: {
    fontSize: 14,
    color: "white",
    opacity: 0.8,
  },

  // Tenant cards
  separator: { height: 8 },
  tenantCard: {},
  tenantHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  tenantInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  tenantName: {
    fontSize: 16,
    fontWeight: "600",
  },
  tenantId: {
    fontSize: 12,
    marginBottom: 4,
  },
  tenantDate: {
    fontSize: 12,
  },

  errorText: { marginTop: 6, color: "#ff6b6b", fontWeight: "600" },
  muted: { opacity: 0.6, textAlign: "center", paddingVertical: 24 },
});
